/**
 * Utility to get dynamic environment variables that are set based on the current URL
 * Works with Next.js 15 and the updated server.js implementation
 */

export function getDynamicEnvFromQuery(query) {
  return {
    countryCode: query._countryCode || process.env.DEV_COUNTRY_CODE || 'AU',
    assignedDomain: query._assignedDomain || process.env.DEV_ASSIGNED_DOMAIN || 'jayco.com.au',
    fullUrl: query._fullUrl || process.env.FULL_URL || 'jayco.com.au'
  };
}

export function getDynamicEnvFromHeaders(req) {
  if (!req || !req.headers) {
    return {
      countryCode: process.env.DEV_COUNTRY_CODE || 'AU',
      assignedDomain: process.env.DEV_ASSIGNED_DOMAIN || 'jayco.com.au',
      fullUrl: process.env.FULL_URL || 'jayco.com.au'
    };
  }

  return {
    countryCode: req.headers['x-country-code'] || process.env.DEV_COUNTRY_CODE || 'AU',
    assignedDomain: req.headers['x-assigned-domain'] || process.env.DEV_ASSIGNED_DOMAIN || 'jayco.com.au',
    fullUrl: req.headers['x-full-url'] || process.env.FULL_URL || 'jayco.com.au'
  };
}

export function getDynamicEnv(context) {

  if(process.env.NEXT_STATIC_EXPORT === 'true' && !process.env.DEALER_SITE) {
    const domainToTest = process.env.STATICLY_DEPLOYMENT_DESTINATION_DOMAIN;
    const countryCode = (domainToTest.indexOf('localhost') !== -1 || domainToTest.indexOf('com-au') !== -1 || domainToTest.indexOf('com.au') !== -1) ? 'AU' : 'NZ';
    const assignedDomain = !process.env.DEALER_SITE ? (countryCode === 'AU' ? 'jayco.com.au' : 'jayco.co.nz') : countryCode === 'AU' ? 'AU-dealers' : 'NZ-dealers';
    
    return {
      countryCode,
      assignedDomain,
      fullUrl: assignedDomain
    };
  }

  // For API routes
  if (context.req) {
    return getDynamicEnvFromHeaders(context.req);
  }
  
  // For getServerSideProps or getInitialProps
  if (context.query) {
    return getDynamicEnvFromQuery(context.query);
  }
  
  // Fallback for client-side
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for local testing override first
    const testDomain = urlParams.get('_testDomain');
    if (testDomain) {
      const countryCode = testDomain.includes('com.au') ? 'AU' : 'NZ';
      const assignedDomain = countryCode === 'AU' ? 'jayco.com.au' : 'jayco.co.nz';
      console.log(`🧪 Client-side test mode: ${testDomain} (${countryCode})`);
      
      return {
        countryCode,
        assignedDomain,
        fullUrl: testDomain
      };
    }
    
    if(process.env.NEXT_STATIC_EXPORT === 'true') {
      const domainToTest = process.env.STATICLY_DEPLOYMENT_DESTINATION_DOMAIN;
      return {
        countryCode: (domainToTest.indexOf('localhost') !== -1 || domainToTest.indexOf('com-au') !== -1 || domainToTest.indexOf('com.au') !== -1) ? 'AU' : 'NZ',
        assignedDomain: !process.env.DEALER_SITE ? (countryProps.countryCode === 'AU' ? 'jayco.com.au' : 'jayco.co.nz') : countryProps.countryCode === 'AU' ? 'AU-dealers' : 'NZ-dealers'
      };
    } else {
      return {
        countryCode: urlParams.get('_countryCode') || 'AU',
        assignedDomain: urlParams.get('_assignedDomain') || 'jayco.com.au',
        fullUrl: urlParams.get('_fullUrl') || window.location.host
      };
    }
  }
  
  // Final fallback
  return {
    countryCode: process.env.DEV_COUNTRY_CODE || 'AU',
    assignedDomain: process.env.DEV_ASSIGNED_DOMAIN || 'jayco.com.au',
    fullUrl: process.env.FULL_URL || 'jayco.com.au'
  };
}

/**
 * Helper function specifically for local testing
 * This makes it easy to simulate different domains during development
 */
export function getLocalTestingHelpers() {
  return {
    testAU: () => {
      console.log('🇦🇺 Testing AU domain: Add ?_testDomain=jayco.com.au to your URL');
      return '?_testDomain=jayco.com.au';
    },
    testNZ: () => {
      console.log('🇳🇿 Testing NZ domain: Add ?_testDomain=jayco.co.nz to your URL'); 
      return '?_testDomain=jayco.co.nz';
    },
    getCurrentMode: () => {
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const testDomain = urlParams.get('_testDomain');
        
        if (testDomain) {
          return `Testing: ${testDomain} (${testDomain.includes('com.au') ? 'AU' : 'NZ'})`;
        }
        
        return 'Default mode (AU)';
      }
      
      return 'Server-side';
    }
  };
}

export default getDynamicEnv;
